import React, { useEffect, useState, useRef, lazy } from 'react';
type DeferredMountProps = {
  children: React.ReactNode;
  /** rootMargin for IntersectionObserver — how far before viewport to mount */
  rootMargin?: string;
  /** Minimum height reserved before mount, prevents CLS */
  minHeight?: number;
  /** Also mount on first user interaction (scroll/touch/pointer) */
  mountOnInteraction?: boolean;
};
/**
 * Defers rendering of children until a sentinel scrolls near the viewport
 * (or, optionally, on first user interaction). Used to defer fetching of
 * lazy() chunks below the fold so the initial page is as light as possible.
 */
export function DeferredMount({
  children,
  rootMargin = '600px',
  minHeight = 400,
  mountOnInteraction = false
}: DeferredMountProps) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [shouldMount, setShouldMount] = useState(false);
  useEffect(() => {
    if (shouldMount) return;
    let observer: IntersectionObserver | null = null;
    let interactionListeners: Array<() => void> = [];
    const mount = () => {
      setShouldMount(true);
      cleanup();
    };
    const cleanup = () => {
      if (observer) observer.disconnect();
      interactionListeners.forEach(off => off());
      interactionListeners = [];
    };
    if (sentinelRef.current && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        if (entries.some(e => e.isIntersecting)) mount();
      }, {
        rootMargin
      });
      observer.observe(sentinelRef.current);
    } else {
      // Fallback: mount when idle
      const t = setTimeout(mount, 1500);
      interactionListeners.push(() => clearTimeout(t));
    }
    if (mountOnInteraction) {
      const events: Array<keyof WindowEventMap> = ['scroll', 'pointerdown', 'touchstart', 'keydown'];
      events.forEach(ev => {
        const handler = () => mount();
        window.addEventListener(ev, handler, {
          passive: true,
          once: true
        });
        interactionListeners.push(() => window.removeEventListener(ev, handler));
      });
    }
    return cleanup;
  }, [shouldMount, rootMargin, mountOnInteraction]);
  return <div ref={sentinelRef} style={!shouldMount ? {
    minHeight
  } : undefined} data-id="element-77">
      {shouldMount ? children : null}
    </div>;
}